import React from 'react'
import { useNavigate } from 'react-router'
import { IconUsers, IconLockOpen, IconTemplate } from '@tabler/icons-react'
import Typography from '../../../atoms/Typography'
import { ROUTES } from 'src/types/routes'

interface UpgradeMessageProps {
  currentUsers?: number
  maxUsers?: number
  onMinimize?: () => void
}

const UpgradeMessage: React.FC<UpgradeMessageProps> = ({ currentUsers = 5, maxUsers = 5, onMinimize }) => {
  const navigate = useNavigate()
  const usagePercentage = (currentUsers / maxUsers) * 100

  const handleUpgrade = () => {
    navigate(ROUTES.SUBS)
    // Minimize Copa window after navigating
    if (onMinimize) {
      onMinimize()
    }
  }

  return (
    <div className="flex w-full flex-col gap-[10px] p-3">
      {/* Message Title */}
      <Typography variant="sm-medium" className="whitespace-pre-wrap leading-[20px] text-[#EBF4F1]">
        Hey! It appears you&apos;ve reached your limit of {maxUsers} team members. Upgrade your plan to add more users
        and collaborate with your entire team.
      </Typography>

      {/* Upgrade Card */}
      <div className="flex w-full flex-col gap-4 rounded-[8px] border border-[#656E75] bg-[#1B1C1E] px-6 py-4">
        {/* Current Usage Section */}
        <div className="flex w-full flex-col gap-4 rounded-[8px] bg-[#27292C] p-4">
          <div className="flex w-full items-start justify-between">
            <Typography variant="xs" className="leading-[16px] text-[#808990]">
              Current Usage
            </Typography>
            <Typography variant="xs" className="text-right leading-[16px] text-[#F5F6F6]">
              {currentUsers} / {maxUsers} Users
            </Typography>
          </div>
          {/* Progress Bar */}
          <div className="h-[8px] w-full overflow-hidden rounded-full bg-[#1B1C1E]">
            <div
              className="h-full bg-[#329175] transition-all duration-300"
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            />
          </div>
        </div>

        {/* Feature Items */}
        <div className="flex flex-col gap-4">
          {/* Unlimited team members */}
          <div className="flex w-full items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center">
              <IconUsers className="h-6 w-6 text-[#329175]" stroke={1.5} />
            </div>
            <div className="flex flex-1 flex-col gap-0">
              <Typography variant="sm-bold" className="leading-[20px] text-[#CDD1D4]">
                Unlimited team members
              </Typography>
              <Typography variant="sm" className="leading-[20px] text-[#808990]">
                Invite your entire team to collaborate seamlessly
              </Typography>
            </div>
          </div>

          {/* Advanced permission controls */}
          <div className="flex w-full items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center">
              <IconLockOpen className="h-6 w-6 text-[#329175]" stroke={1.5} />
            </div>
            <div className="flex flex-1 flex-col gap-0">
              <Typography variant="sm-bold" className="leading-[20px] text-[#CDD1D4]">
                Advanced permission controls
              </Typography>
              <Typography variant="sm" className="leading-[20px] text-[#808990]">
                Manage team roles and access levels with precision
              </Typography>
            </div>
          </div>
        </div>
      </div>

      {/* White Labeling Info Section */}
      <div className="flex w-full flex-col gap-4 rounded-[8px] border border-[#329175] bg-[rgba(50,145,117,0.2)] p-4">
        <div className="flex w-full items-start gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center">
            <IconTemplate className="h-6 w-6 text-[#329175]" stroke={1.5} />
          </div>
          <div className="flex flex-1 flex-col">
            <Typography variant="sm-medium" className="leading-[20px] text-[#CDD1D4]">
              Greater collaboration is available on our Enterprise plan.
            </Typography>
            <Typography variant="sm-medium" className="leading-[20px] text-[#CDD1D4]">
              Perfect for agencies, consultants, and businesses serving clients.
            </Typography>
          </div>
        </div>

        {/* Upgrade Plan Button */}
        <button
          onClick={handleUpgrade}
          className="flex h-12 w-full items-center justify-center gap-3 rounded-[100px] bg-[#329175] px-8 py-4 transition-all duration-200 hover:brightness-110"
        >
          <IconTemplate className="h-5 w-5 text-[#F5F6F6]" stroke={1.5} />
          <Typography variant="base-medium" className="leading-[24px] text-[#F5F6F6]">
            Upgrade Plan
          </Typography>
        </button>
      </div>
    </div>
  )
}

export default UpgradeMessage
